import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, LogOut, Monitor, ShieldAlert, Trash2 } from "lucide-react";
import { api, apiError, logError } from "@/lib/api";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Empty } from "@/components/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const shortDevice = (ua) => {
  if (!ua) return "Unknown device";
  const os = /Android/i.test(ua) ? "Android" : /iPhone|iPad/i.test(ua) ? "iPhone/iPad"
    : /Windows/i.test(ua) ? "Windows" : /Mac OS/i.test(ua) ? "Mac" : /Linux/i.test(ua) ? "Linux" : "Device";
  const browser = /Edg/i.test(ua) ? "Edge" : /Chrome/i.test(ua) ? "Chrome"
    : /Safari/i.test(ua) ? "Safari" : /Firefox/i.test(ua) ? "Firefox" : "Browser";
  return `${os} • ${browser}`;
};

export const Devices = ({ employees = [] }) => {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/devices${filter ? `?user_id=${filter}` : ""}`)
      .then(({ data: d }) => setData(d)).catch((e) => logError("devices", e));
    api.get("/devices/settings").then(({ data: d }) => setSettings(d))
      .catch((e) => logError("device settings", e));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (device, action) => {
    setBusy(device.id + action);
    try {
      await api.post(`/devices/${device.id}/${action}`);
      toast.success(action === "revoke" ? "Device signed out" : "Device approved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (!data || !settings) {
    return (
      <div className="flex h-40 items-center justify-center" data-testid="devices-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="devices-panel">
      <div className="surface space-y-3 p-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Show devices of">
            <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="device-user-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="New device needs Owner approval">
            <div className="pt-2">
              <Switch checked={settings.require_new_device_approval}
                data-testid="device-approval-toggle"
                onCheckedChange={async (v) => {
                  try {
                    const { data: d } = await api.put("/devices/settings", {
                      require_new_device_approval: v,
                      trust_first_device: settings.trust_first_device,
                    });
                    setSettings(d);
                    toast.success(v ? "New devices will now wait for your approval"
                      : "New devices are allowed without approval");
                  } catch (e) {
                    toast.error(apiError(e));
                  }
                }} />
            </div>
          </Field>
          <Field label="Trust the employee's first device automatically">
            <div className="pt-2">
              <Switch checked={settings.trust_first_device} data-testid="device-trust-first-toggle"
                onCheckedChange={async (v) => {
                  try {
                    const { data: d } = await api.put("/devices/settings", {
                      require_new_device_approval: settings.require_new_device_approval,
                      trust_first_device: v,
                    });
                    setSettings(d);
                    toast.success("Saved");
                  } catch (e) {
                    toast.error(apiError(e));
                  }
                }} />
            </div>
          </Field>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="device-otp-note">
          {settings.otp_note}
        </p>
        {data.pending > 0 && (
          <Badge variant="destructive" className="gap-1 text-xs" data-testid="device-pending-badge">
            <ShieldAlert className="h-3 w-3" /> {data.pending} device(s) awaiting approval
          </Badge>
        )}
      </div>

      {data.devices.length === 0 ? (
        <Empty title="No devices recorded yet" hint="A device appears here the first time someone signs in from it" />
      ) : (
        <div className="space-y-2" data-testid="device-list">
          {data.devices.map((d) => (
            <div key={d.id} className="surface flex flex-wrap items-center justify-between gap-3 p-4"
              data-testid={`device-row-${d.id}`}>
              <div>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Monitor className="h-4 w-4 text-primary" /> {d.user_name}
                  <span className="text-xs text-muted-foreground">{shortDevice(d.user_agent)}</span>
                  {d.trusted ? (
                    <Badge variant="outline" className="text-[10px]"
                      data-testid={`device-trusted-${d.id}`}>approved</Badge>
                  ) : (
                    <Badge variant="destructive" className="text-[10px]"
                      data-testid={`device-untrusted-${d.id}`}>needs approval</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {d.method} sign-in • {d.login_count} login(s) • first seen{" "}
                  {String(d.first_seen).slice(0, 16).replace("T", " ")} • last seen{" "}
                  {String(d.last_seen).slice(0, 16).replace("T", " ")}
                  {d.ip ? ` • ${d.ip}` : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {!d.trusted && (
                  <Button size="sm" className="gap-1.5" disabled={!!busy}
                    data-testid={`device-approve-${d.id}`} onClick={() => act(d, "approve")}>
                    <Check className="h-3.5 w-3.5" /> Approve
                  </Button>
                )}
                <Button size="sm" variant="destructive" className="gap-1.5" disabled={!!busy}
                  data-testid={`device-revoke-${d.id}`} onClick={() => act(d, "revoke")}>
                  <Trash2 className="h-3.5 w-3.5" /> Revoke
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" disabled={!!busy}
                  data-testid={`device-logout-all-${d.user_id}`}
                  onClick={async () => {
                    if (!window.confirm(`Sign ${d.user_name} out of every device?`)) return;
                    try {
                      const { data: r } = await api.post(`/devices/users/${d.user_id}/logout-all`);
                      toast.success(`${r.devices_revoked} device(s) signed out`);
                      load();
                    } catch (e) {
                      toast.error(apiError(e));
                    }
                  }}>
                  <LogOut className="h-3.5 w-3.5" /> Logout all
                </Button>
                <Button size="sm" variant="outline" disabled={!!busy}
                  data-testid={`device-block-${d.user_id}`}
                  onClick={async () => {
                    const blocked = data.blocked_users.some((b) => b.user_id === d.user_id);
                    try {
                      await api.post(`/devices/users/${d.user_id}/login-block?blocked=${!blocked}`);
                      toast.success(blocked ? "Login unblocked" : "Login blocked");
                      load();
                    } catch (e) {
                      toast.error(apiError(e));
                    }
                  }}>
                  {data.blocked_users.some((b) => b.user_id === d.user_id) ? "Unblock login" : "Block login"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="surface p-4" data-testid="login-events">
        <div className="overline mb-1">Recent sign-ins</div>
        {data.login_events.length === 0 ? (
          <p className="text-xs text-muted-foreground">No sign-ins recorded yet.</p>
        ) : (
          data.login_events.slice(0, 15).map((e) => (
            <div key={e.id} className="flex flex-wrap justify-between gap-2 border-t border-border py-1.5 text-xs">
              <span>
                {e.user_name} • {e.method}
                {e.new_device ? " • new device" : ""}
              </span>
              <span className="text-muted-foreground">
                {e.outcome} • {String(e.created_at).slice(0, 16).replace("T", " ")}
                {e.ip ? ` • ${e.ip}` : ""}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
